import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface OnlineDonationContent {
  enabled: boolean;
  purposeLabel: string;
  purpose1: string;
  purpose2: string;
  purpose3: string;
  purpose4: string;
  purpose5: string;
}

export default function OnlineDonationManager() {
  const [content, setContent] = useState<OnlineDonationContent>({
    enabled: true,
    purposeLabel: 'Purpose of Donation',
    purpose1: 'General Fund',
    purpose2: 'Annadanam (Food Distribution)',
    purpose3: 'Temple Renovation',
    purpose4: 'Festival Contribution',
    purpose5: 'Other'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, []);

  const loadContent = () => {
    try {
      console.log('🔄 OnlineDonationManager: Setting up real-time listener...');
      const docRef = doc(db, 'online_donation_content', 'main');
      setLoading(true);
      
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data() as OnlineDonationContent;
            setContent(data);
            console.log('✅ Admin: Real-time content loaded from Firebase');
          } else {
            console.warn('⚠️ Admin: Document does not exist, using defaults');
          }
        } catch (error) {
          console.error('Error processing content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Admin Error in onSnapshot:', error);
        setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
        setLoading(false);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
      setLoading(false);
      return () => {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving online donation content to Firebase...');
      const docRef = doc(db, 'online_donation_content', 'main');
      await setDoc(docRef, content);
      console.log('✅ Content saved successfully to online_donation_content/main');
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof OnlineDonationContent, value: string | boolean) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Online Donation Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage the online donation page content and form fields
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🔘 Online Donation Status
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={content.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              color="primary"
            />
          }
          label={content.enabled ? 'Online Donation is Enabled' : 'Online Donation is Disabled'}
        />
        {!content.enabled && (
          <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1 }}>
            Online donation form will be hidden from devotees. They will see a message to contact the temple office instead.
          </Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📋 Purpose of Donation
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Purpose Label" value={content.purposeLabel} onChange={(e) => handleChange('purposeLabel', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} key={i}>
              <TextField fullWidth label={`Purpose ${i}`} value={content[`purpose${i}` as keyof OnlineDonationContent]} onChange={(e) => handleChange(`purpose${i}` as keyof OnlineDonationContent, e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity as 'success' | 'error'} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
